/**
 * LocalWidgetDetailSections — detail sheet components for local utility widgets.
 *
 * Split from TileDetailSections.tsx to keep file sizes bounded (<800 lines each).
 * Contains: NoteDetail, TodoDetail, CountdownDetail — all edit-in-place sections
 * for the note.sticky, todo.list, and countdown.event tile types (D-015).
 *
 * Editing UX decisions:
 *   NoteDetail: EXPLICIT SAVE via a Save button (not debounce). Notes can be
 *     long; debouncing fires configureTile on every keystroke. Save button
 *     signals unsaved state clearly.
 *   TodoDetail: IMMEDIATE commit per mutation (toggle/add/delete). The optimistic
 *     local-items state ensures the UI stays fresh even though App.tsx passes a
 *     snapshot of the tile at sheet-open time.
 *   CountdownDetail: EXPLICIT SAVE (same as note). Countdown edits are infrequent.
 *
 * Immutability: all state transitions create new objects, never mutate.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Tile } from 'briefpane/schema';
import { MAX_NOTE_CHARS, MAX_TODO_ITEMS, MAX_TODO_ITEM_CHARS } from 'briefpane/schema';
import type { GlanceViewModel, AnalyticsEmitter } from 'briefpane/contracts';
import type { CommitError } from '../board/use-editable-board.js';

export interface LocalWidgetDetailProps {
  readonly tile: Tile;
  readonly vm: GlanceViewModel;
  readonly data?: Record<string, unknown>;
  readonly onConfigure: (
    tileId: string,
    newParams: Record<string, unknown>,
    settingKey?: 'sports_team' | 'watchlist_symbols' | 'news_topic' | 'location' | 'other',
  ) => CommitError;
  readonly analytics: AnalyticsEmitter;
}

/* -------------------------------------------------------------------------- */
/* NoteDetail                                                                   */
/* -------------------------------------------------------------------------- */

export function NoteDetail({ tile, onConfigure }: LocalWidgetDetailProps): React.ReactElement {
  const params = tile.source?.params as { text?: string } | undefined;
  const savedText = params?.text ?? '';
  const [text, setText] = useState<string>(savedText);
  const [dirty, setDirty] = useState(false);

  // Sync when tile params change (e.g. another session configured it).
  useEffect(() => {
    setText(savedText);
    setDirty(false);
  }, [savedText]);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    const newText = e.target.value;
    setText(newText);
    setDirty(newText !== savedText);
  }

  function handleSave(): void {
    onConfigure(tile.id, { text }, 'other');
    setDirty(false);
  }

  const overLimit = text.length > MAX_NOTE_CHARS;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <textarea
        value={text}
        onChange={handleChange}
        maxLength={MAX_NOTE_CHARS}
        placeholder="Start writing..."
        style={{
          width: '100%',
          minHeight: '160px',
          padding: '8px',
          fontSize: 'var(--text-detail)',
          color: 'var(--color-text-primary)',
          backgroundColor: 'var(--color-bg-input)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-button)',
          resize: 'vertical',
          boxSizing: 'border-box',
          fontFamily: 'inherit',
          lineHeight: 1.5,
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontSize: 'var(--text-label)',
          color: overLimit ? 'var(--color-negative-accent)' : 'var(--color-text-dim)',
        }}>
          {text.length} / {MAX_NOTE_CHARS}
        </span>
        <button
          type="button"
          disabled={!dirty || overLimit}
          onClick={handleSave}
          style={{
            background: dirty && !overLimit ? 'var(--color-accent)' : 'none',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-button)',
            color: dirty && !overLimit ? '#fff' : 'var(--color-text-dim)',
            fontSize: 'var(--text-label)',
            fontWeight: 500,
            padding: '6px 16px',
            cursor: dirty && !overLimit ? 'pointer' : 'not-allowed',
            opacity: dirty && !overLimit ? 1 : 0.6,
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* TodoDetail                                                                   */
/* -------------------------------------------------------------------------- */

export function TodoDetail({ tile, onConfigure }: LocalWidgetDetailProps): React.ReactElement {
  const params = tile.source?.params as { items?: Array<{ text: string; done: boolean }> } | undefined;
  const initialItems = params?.items ?? [];

  // Local copy of items — maintained optimistically so the UI reflects each commit
  // immediately without needing the tile prop to update (App passes a snapshot).
  const [items, setItems] = useState<Array<{ text: string; done: boolean }>>(initialItems);
  const [newItemText, setNewItemText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const atLimit = items.length >= MAX_TODO_ITEMS;

  function commitItems(newItems: Array<{ text: string; done: boolean }>): void {
    setItems(newItems); // optimistic UI update
    onConfigure(tile.id, { items: newItems }, 'other');
  }

  function handleToggle(idx: number): void {
    const updated = items.map((item, i) =>
      i === idx ? { ...item, done: !item.done } : item,
    );
    commitItems(updated);
  }

  function handleDelete(idx: number): void {
    const updated = items.filter((_, i) => i !== idx);
    commitItems(updated);
  }

  function handleAdd(): void {
    const trimmed = newItemText.trim();
    if (!trimmed || atLimit) return;
    if (trimmed.length > MAX_TODO_ITEM_CHARS) return;
    const updated = [...items, { text: trimmed, done: false }];
    commitItems(updated);
    setNewItemText('');
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  }, [newItemText, items, atLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputTooLong = newItemText.length > MAX_TODO_ITEM_CHARS;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {items.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-detail)', color: 'var(--color-text-dim)' }}>
          No items yet. Add one below.
        </p>
      ) : (
        <ul role="list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {items.map((item, idx) => (
            <li
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2)',
                background: 'var(--color-bg)',
                borderRadius: 'var(--radius-button)',
                border: '1px solid var(--color-border)',
              }}
            >
              <input
                type="checkbox"
                checked={item.done}
                onChange={() => handleToggle(idx)}
                style={{ cursor: 'pointer', flexShrink: 0 }}
                aria-label={`Mark "${item.text}" as ${item.done ? 'not done' : 'done'}`}
              />
              <span style={{
                flex: 1,
                fontSize: 'var(--text-detail)',
                color: item.done ? 'var(--color-text-dim)' : 'var(--color-text-primary)',
                textDecoration: item.done ? 'line-through' : 'none',
                wordBreak: 'break-word',
              }}>
                {item.text}
              </span>
              <button
                type="button"
                onClick={() => handleDelete(idx)}
                aria-label={`Remove "${item.text}"`}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-dim)',
                  fontSize: 'var(--text-label)',
                  padding: '2px 6px',
                  borderRadius: 'var(--radius-button)',
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {atLimit ? (
          <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--color-text-dim)' }}>
            List is full ({MAX_TODO_ITEMS} items max). Remove items to add more.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <input
                ref={inputRef}
                type="text"
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add an item…"
                maxLength={MAX_TODO_ITEM_CHARS + 1}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: 'var(--text-detail)',
                  color: 'var(--color-text-primary)',
                  backgroundColor: 'var(--color-bg-input)',
                  border: `1px solid ${inputTooLong ? 'var(--color-negative-accent)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-button)',
                  boxSizing: 'border-box',
                }}
                aria-label="New to-do item text"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={!newItemText.trim() || inputTooLong}
                style={{
                  background: 'var(--color-accent)',
                  border: 'none',
                  borderRadius: 'var(--radius-button)',
                  color: '#fff',
                  fontSize: 'var(--text-label)',
                  fontWeight: 500,
                  padding: '6px 14px',
                  cursor: newItemText.trim() && !inputTooLong ? 'pointer' : 'not-allowed',
                  opacity: newItemText.trim() && !inputTooLong ? 1 : 0.5,
                }}
              >
                Add
              </button>
            </div>
            {inputTooLong && (
              <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-negative-accent)' }}>
                Item text is too long (max {MAX_TODO_ITEM_CHARS} characters)
              </span>
            )}
          </>
        )}
        <p style={{ margin: 0, fontSize: 'var(--text-timestamp)', color: 'var(--color-text-dim)' }}>
          Reordering items will be available in a future update.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* CountdownDetail                                                              */
/* -------------------------------------------------------------------------- */

export function CountdownDetail({ tile, onConfigure }: LocalWidgetDetailProps): React.ReactElement {
  const params = tile.source?.params as { label?: string; targetISO?: string } | undefined;
  const savedLabel = params?.label ?? '';
  const savedTargetISO = params?.targetISO ?? '';

  const [label, setLabel] = useState(savedLabel);
  const toLocalValue = (iso: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [targetLocal, setTargetLocal] = useState(() => toLocalValue(savedTargetISO));
  const [dirty, setDirty] = useState(false);
  const [liveRemaining, setLiveRemaining] = useState<string>('');

  useEffect(() => {
    function updateRemaining(): void {
      if (!savedTargetISO) return;
      const targetMs = Date.parse(savedTargetISO);
      if (isNaN(targetMs)) return;
      const diffMs = targetMs - Date.now();
      const absDays = Math.abs(Math.floor(diffMs / 86_400_000));
      const absHours = Math.abs(Math.floor(diffMs / 3_600_000));
      const absMin = Math.abs(Math.floor(diffMs / 60_000));
      const isPast = diffMs <= 0;
      if (absDays >= 2) {
        setLiveRemaining(isPast ? `${absDays} days ago` : `${absDays} days`);
      } else if (absHours >= 2) {
        setLiveRemaining(isPast ? `${absHours} hours ago` : `${absHours} hours`);
      } else {
        setLiveRemaining(isPast ? `${absMin} minutes ago` : `${absMin} minutes`);
      }
    }
    updateRemaining();
    const id = setInterval(updateRemaining, 60_000);
    return () => clearInterval(id);
  }, [savedTargetISO]);

  useEffect(() => {
    setLabel(savedLabel);
    setTargetLocal(toLocalValue(savedTargetISO));
    setDirty(false);
  }, [savedLabel, savedTargetISO]);

  function handleChange(field: 'label' | 'targetLocal', value: string): void {
    if (field === 'label') setLabel(value);
    else setTargetLocal(value);
    setDirty(true);
  }

  function handleSave(): void {
    if (!label.trim() || !targetLocal) return;
    const d = new Date(targetLocal);
    if (isNaN(d.getTime())) return;
    const targetISO = d.toISOString();
    onConfigure(tile.id, { label: label.trim(), targetISO }, 'other');
    setDirty(false);
  }

  const labelOver = label.length > 40;
  const canSave = dirty && !labelOver && !!targetLocal && !!label.trim();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {liveRemaining && (
        <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
          {liveRemaining}
        </div>
      )}

      <div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
          Event label
          <input
            type="text"
            value={label}
            maxLength={41}
            onChange={(e) => handleChange('label', e.target.value)}
            style={{
              marginTop: '4px',
              padding: '6px 8px',
              fontSize: 'var(--text-detail)',
              color: 'var(--color-text-primary)',
              backgroundColor: 'var(--color-bg-input)',
              border: `1px solid ${labelOver ? 'var(--color-negative-accent)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-button)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
        </label>
        {labelOver && (
          <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-negative-accent)' }}>
            Label is too long (max 40 characters)
          </span>
        )}
      </div>

      <div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
          Target date and time
          <input
            type="datetime-local"
            value={targetLocal}
            onChange={(e) => handleChange('targetLocal', e.target.value)}
            style={{
              marginTop: '4px',
              padding: '6px 8px',
              fontSize: 'var(--text-detail)',
              color: 'var(--color-text-primary)',
              backgroundColor: 'var(--color-bg-input)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-button)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
        </label>
      </div>

      <button
        type="button"
        disabled={!canSave}
        onClick={handleSave}
        style={{
          background: canSave ? 'var(--color-accent)' : 'none',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-button)',
          color: canSave ? '#fff' : 'var(--color-text-dim)',
          fontSize: 'var(--text-label)',
          fontWeight: 500,
          padding: '6px 16px',
          cursor: canSave ? 'pointer' : 'not-allowed',
          opacity: canSave ? 1 : 0.6,
          alignSelf: 'flex-start',
        }}
      >
        Save
      </button>
    </div>
  );
}
